import { levelFromTimeDomain } from './level.js';
import type { PcmStream } from './spoken-audio.js';

/**
 * Plays speech as it arrives (2026-09-21). A provider that streams has its first samples ready a
 * second or so after it is asked and its last ones several seconds later; this schedules each piece
 * on the Web Audio clock directly behind the one before it, so a person hears the first word while
 * the last is still being made.
 *
 * Each piece becomes one buffer source started at an exact time, which is what makes the joins
 * inaudible: a gap or an overlap of a few samples is a click. Pieces are gathered to a tenth of a
 * second before they are scheduled, so a long reply is a few dozen nodes rather than hundreds. The
 * first piece starts a little ahead of "now" so the second has time to arrive behind it.
 *
 * The listener's own speed applies here too. A buffer source has no pitch correction, so a faster
 * reading is also a slightly higher one — the same thing that happens to the whole-file player in a
 * browser without `preservesPitch`.
 *
 * Nothing is kept: a piece is handed to the audio graph and dropped. Stopping ends every scheduled
 * source, lets go of the network stream and closes the context, whether playback ended, was
 * stopped, or failed half way — in which case what arrived is what was heard.
 */
export interface PcmPlayerSeams {
  readonly audioContext: new () => AudioContext;
  readonly requestFrame: (callback: () => void) => number;
  readonly cancelFrame: (handle: number) => void;
  readonly rate: () => number;
}

/** How far ahead of the clock the first piece starts, so the next can arrive behind it. */
export const FIRST_PIECE_LEAD_SECONDS = 0.08;
/** A tenth of a second of 16-bit mono at the stream's own rate is gathered before scheduling. */
const SECONDS_PER_BUFFER = 0.1;

function floatsFrom(bytes: Uint8Array): Float32Array {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const samples = new Float32Array(bytes.byteLength >> 1);
  for (let index = 0; index < samples.length; index += 1)
    samples[index] = view.getInt16(index * 2, true) / 32768;
  return samples;
}

export async function playPcmStream(
  seams: PcmPlayerSeams,
  stream: PcmStream,
  onLevel?: (level: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const context = new seams.audioContext();
  const analyser = context.createAnalyser();
  analyser.fftSize = 512;
  analyser.connect(context.destination);
  // A press started this, which is what autoplay policy wants; resume covers a suspended start.
  void context.resume().catch(() => undefined);

  const rate = seams.rate();
  const reader = stream.chunks.getReader();
  const live = new Set<AudioBufferSourceNode>();
  const bytesPerBuffer = Math.max(2, Math.floor(stream.sampleRate * SECONDS_PER_BUFFER) * 2);
  let nextStart = 0;
  let stopped = false;
  /** Read through a function: `stop` runs from an event, which a narrowed `let` cannot see. */
  const isStopped = (): boolean => stopped;
  let frame = 0;

  if (onLevel !== undefined) {
    const samples = new Uint8Array(analyser.frequencyBinCount);
    const tick = (): void => {
      analyser.getByteTimeDomainData(samples);
      onLevel(levelFromTimeDomain(samples));
      frame = seams.requestFrame(tick);
    };
    frame = seams.requestFrame(tick);
  }

  let settle: () => void = () => undefined;
  const drained = new Promise<void>((resolve) => {
    settle = resolve;
  });
  let readingDone = false;
  const maybeSettle = (): void => {
    if (readingDone && live.size === 0) settle();
  };

  const schedule = (bytes: Uint8Array): void => {
    const samples = floatsFrom(bytes);
    if (samples.length === 0) return;
    const buffer = context.createBuffer(1, samples.length, stream.sampleRate);
    buffer.getChannelData(0).set(samples);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = rate;
    source.connect(analyser);
    const at = Math.max(
      nextStart,
      context.currentTime + (nextStart === 0 ? FIRST_PIECE_LEAD_SECONDS : 0),
    );
    nextStart = at + buffer.duration / rate;
    live.add(source);
    source.onended = () => {
      live.delete(source);
      maybeSettle();
    };
    source.start(at);
  };

  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    for (const source of live) {
      source.onended = null;
      try {
        source.stop();
      } catch {
        // A source that never started cannot be stopped; it is dropped with the context.
      }
    }
    live.clear();
    void reader.cancel().catch(() => undefined);
    settle();
  };
  // Abort is a stop, not an error: the listener chose to end playback.
  signal?.addEventListener('abort', stop);

  let pending = new Uint8Array(0);
  try {
    for (;;) {
      if (isStopped() || signal?.aborted === true) break;
      const { done, value } = await reader.read();
      if (done) break;
      const joined = new Uint8Array(pending.length + value.length);
      joined.set(pending, 0);
      joined.set(value, pending.length);
      pending = joined;
      while (pending.length >= bytesPerBuffer && !isStopped()) {
        schedule(pending.subarray(0, bytesPerBuffer));
        pending = pending.slice(bytesPerBuffer);
      }
    }
  } catch {
    // The stream broke half way: what arrived is what is heard.
  }
  if (signal?.aborted === true) stop();
  // The tail, to a whole sample; an orphaned byte is dropped rather than padded into a click.
  if (!isStopped() && pending.length >= 2)
    schedule(pending.subarray(0, pending.length - (pending.length % 2)));
  readingDone = true;
  maybeSettle();

  await drained;
  if (onLevel !== undefined) seams.cancelFrame(frame);
  void reader.cancel().catch(() => undefined);
  await context.close().catch(() => undefined);
}
